import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CreditCard, Banknote, ArrowRightLeft, Info, CircleCheck } from 'lucide-react';
import CustomModal from './CustomModal';

const PAYMENT_METHODS = [
  { key: 'card', label: 'Tarjeta', Icon: CreditCard },
  { key: 'cash', label: 'Efectivo', Icon: Banknote },
  { key: 'transfer', label: 'Transferencia', Icon: ArrowRightLeft },
];

// Predefined quick-amount presets for the "Añadir saldo" form (pre-fill only)
const QUICK_ADD_AMOUNTS = [10, 20, 50, 100];
const QUICK_REFUND_AMOUNTS = [10, 20, 50];

// Formato monetario es-ES. Devuelve '' para valores no numéricos, para que quien
// lo use tenga que decidir explícitamente qué pintar en vez de heredar un 0.
const eur = (n) => Number.isFinite(Number(n))
  ? Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : '';

function formatApiErrorDetail(detail) {
  if (!detail) return 'Ocurrió un error durante la transacción';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => formatApiErrorDetail(item)).filter(Boolean).join(' ');
  }
  if (typeof detail === 'object') {
    return Object.entries(detail)
      .map(([field, value]) => {
        const message = formatApiErrorDetail(value);
        return field === 'non_field_errors' || field === 'detail' ? message : `${field}: ${message}`;
      })
      .filter(Boolean)
      .join(' ');
  }
  return 'Ocurrió un error durante la transacción';
}

export default function AddCashModal({
  active,
  handleAddCashModal,
  venueID,
  token,
  user_id,
  setErrorMessage,
  onCashAdded,
  currentBalance = 0,
  memberName,
  memberNumber,
  memberStatus,
  memberImageUrl,
}) {
  const [addQuantity, setAddQuantity] = useState('');
  const [addPaymentMethod, setAddPaymentMethod] = useState('cash');
  const [refundQuantity, setRefundQuantity] = useState('');
  const [refundPaymentMethod, setRefundPaymentMethod] = useState('cash');
  const [activeTab, setActiveTab] = useState('add'); // 'add' | 'refund'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [isReversing, setIsReversing] = useState(false);
  const [successAction, setSuccessAction] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setRefundPaymentMethod('cash');
  }, []);

  const submitBalanceAction = async ({ action, amount, paymentMethod }) => {
    setLocalError('');
    setIsSubmitting(true);

    const parsedAmount = Number(amount);
    if (!amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setLocalError('La cantidad debe ser mayor a 0');
      setIsSubmitting(false);
      return;
    }

    if (!user_id || !venueID) {
      setLocalError('Falta información del cliente o venue');
      setIsSubmitting(false);
      return;
    }

    if (action === 'refund' && parsedAmount > Number(currentBalance || 0)) {
      setLocalError('No se puede devolver más saldo del disponible');
      setIsSubmitting(false);
      return;
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        user_id: user_id,
        venue_id: venueID,
        type: action === 'refund' ? 'refund' : 'add_cash',
        amount: parsedAmount,
        status: "pending",
        payment_method: paymentMethod,
      }),
    };

    try {
      const response = await fetch(action === 'refund' ? '/v1/refund-balance' : '/v1/add-cash', requestOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: action === 'refund' ? 'Error al devolver saldo' : 'Error al agregar saldo' }));
        throw errorData;
      }

      const data = await response.json();

      // Si la respuesta no trae un saldo utilizable, se queda en null y NO se
      // pinta. Antes caía a 0, y un 0 inventado es indistinguible de un saldo
      // real de 0 € — en una pantalla de dinero eso es un dato falso.
      // Ojo: Number(null) === 0, así que el null explícito hay que cortarlo antes.
      const nb = data.new_balance != null ? Number(data.new_balance) : NaN;
      setTransactionData({
        action,
        transaction_id: data.transaction_id,
        amount: parsedAmount,
        new_balance: Number.isFinite(nb) ? nb : null,
      });
      setSuccessAction(action);
      setShowSuccess(true);
    } catch (error) {
      console.error(`Error ${action === 'refund' ? 'refunding balance' : 'adding cash'}:`, error);
      const errorMsg = formatApiErrorDetail(error.detail || error);
      setLocalError(errorMsg);
      if (setErrorMessage) {
        setErrorMessage(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const reverseTransaction = async () => {
    if (!transactionData || !transactionData.transaction_id || transactionData.action !== 'add') {
      setLocalError('No se puede revertir la transacción');
      return;
    }
    
    setIsReversing(true);
    setLocalError('');
    
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + token,
        },
      };
      
      const response = await fetch(`/v1/reverse-add-cash/${transactionData.transaction_id}`, requestOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Error al revertir la transacción' }));
        throw errorData;
      }
      
      const data = await response.json();
      
      // Actualizar el balance con el callback
      if (onCashAdded) {
        onCashAdded(user_id, data.new_balance);
      }

      setShowSuccess(false);
      setTransactionData(null);
      setSuccessAction(null);
      setAddQuantity('');
      setRefundQuantity('');
      setLocalError('');

      handleAddCashModal();
    } catch (error) {
      console.error("Error reversing transaction:", error);
      const errorMsg = error.detail || 'Ocurrió un error al revertir la transacción';
      setLocalError(errorMsg);
      if (setErrorMessage) {
        setErrorMessage(errorMsg);
      }
    } finally {
      setIsReversing(false);
    }
  };

  const handleClose = () => {
    if (onCashAdded && transactionData) {
      // Los 9 consumidores de onCashAdded escriben este valor directamente en su
      // estado (p.ej. ClubEntradas hace `credit_balance: newBalance`), así que un
      // null se propagaría y volvería a pintar €0,00 en otra pantalla. Si el saldo
      // confirmado no llegó, se manda la proyección local —el mismo número que el
      // modal ya le enseñó al operario antes de confirmar— en vez de un cero.
      const confirmed = transactionData.new_balance;
      const projected = transactionData.action === 'refund'
        ? Number(currentBalance || 0) - Number(transactionData.amount || 0)
        : Number(currentBalance || 0) + Number(transactionData.amount || 0);
      onCashAdded(user_id, Number.isFinite(confirmed) ? confirmed : projected);
    }

    setShowSuccess(false);
    setTransactionData(null);
    setSuccessAction(null);
    setAddQuantity('');
    setRefundQuantity('');
    setLocalError('');

    handleAddCashModal();
  };

  useEffect(() => {
    if (!active) {
      setShowSuccess(false);
      setTransactionData(null);
      setSuccessAction(null);
      setAddQuantity('');
      setAddPaymentMethod('cash');
      setRefundQuantity('');
      setRefundPaymentMethod('cash');
      setActiveTab('add');
      setLocalError('');
    }
  }, [active]);

  const handleSubmitAdd = (e) => {
    e.preventDefault();
    submitBalanceAction({ action: 'add', amount: addQuantity, paymentMethod: addPaymentMethod });
  };

  const handleSubmitRefund = (e) => {
    e.preventDefault();
    submitBalanceAction({ action: 'refund', amount: refundQuantity, paymentMethod: 'cash' });
  };

  // Figma: Club_BalanceDialog / Confirmación (3829:62153) y su variante Retirar
  // (3832:67408). Panel con los tokens de Club_Toast/success y una fila de dos
  // acciones: Deshacer (Outline) + OK (Primary).
  const successCopy = successAction === 'refund'
    ? { amount: 'Saldo devuelto', balance: 'Saldo restante' }
    : { amount: 'Saldo añadido', balance: 'Nuevo saldo' };

  const successContent = (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-[var(--radius-sm)] border border-[var(--toast-success-border)] bg-[var(--toast-success-bg)] p-3">
        <CircleCheck size={24} className="shrink-0 text-[var(--toast-success-icon)]" aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-1.5">
          <p className="text-sm font-bold text-[var(--toast-success-text)]">
            {successCopy.amount}: €{eur(transactionData?.amount)}
          </p>
          {Number.isFinite(transactionData?.new_balance) ? (
            <p className="text-sm text-[var(--toast-success-text)]">
              {successCopy.balance}: €{eur(transactionData.new_balance)}
            </p>
          ) : (
            <p className="text-sm text-[var(--toast-success-text)]">
              Operación registrada. Refresca la ficha del socio para ver el saldo actualizado.
            </p>
          )}
        </div>
      </div>

      {localError && (
        <div className="rounded-[var(--radius-md)] border border-[var(--toast-error-border)] bg-[var(--toast-error-bg)] px-4 py-3 text-sm text-[var(--toast-error-text)]" role="alert">
          <span className="block sm:inline">{localError}</span>
        </div>
      )}

      <div className="flex gap-3">
        {successAction === 'add' && (
          <button
            onClick={reverseTransaction}
            disabled={isReversing}
            className={`flex-1 inline-flex h-9 items-center justify-center rounded-[var(--radius-sm)] border px-4 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
              isReversing
                ? 'cursor-not-allowed border-[var(--border)] bg-[var(--muted)] text-[var(--muted-foreground)]'
                : 'border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] hover:bg-[var(--muted)]'
            }`}
          >
            {isReversing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Revirtiendo...
              </>
            ) : (
              'Deshacer'
            )}
          </button>
        )}
        <button
          onClick={handleClose}
          className="flex-1 inline-flex h-9 items-center justify-center rounded-[var(--radius-sm)] px-4 text-sm font-medium text-[var(--primary-foreground)] bg-[var(--primary)] transition-colors hover:bg-[var(--primary-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2"
        >
          OK
        </button>
      </div>
    </div>
  );

  const renderBalanceForm = ({
    action,
    inputId,
    amount,
    setAmount,
    paymentMethod,
    setPaymentMethod,
    paymentMethods,
    onSubmit,
    submitLabel,
    helperText,
    amountRef,
    autoFocus = false,
    extraInfo,
    quickAmounts,
    intro,
    showPaymentMethods = true,
  }) => {
    const parsedAmount = Number(amount) || 0;
    const base = Number(currentBalance || 0);
    const projectedBalance = action === 'refund' ? base - parsedAmount : base + parsedAmount;
    const fmt = (n) => Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
    <form onSubmit={onSubmit} className="space-y-4">
      {intro && <p className="text-sm text-[var(--muted-foreground)]">{intro}</p>}
      {extraInfo}
      {showPaymentMethods && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)] mb-2">Método de pago</label>
          <div className="grid grid-cols-3 gap-2">
            {paymentMethods.map((method) => {
              const isSelected = paymentMethod === method.key;
              return (
                <button
                  key={method.key}
                  type="button"
                  onClick={() => setPaymentMethod(method.key)}
                  disabled={isSubmitting}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border py-3 text-xs font-medium transition-colors ${
                    isSelected
                      ? 'border-[var(--primary)] bg-white text-[var(--foreground)] ring-1 ring-[var(--primary)]'
                      : 'border-[var(--border)] bg-[var(--surface-subtle)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]'
                  } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <method.Icon size={18} className={isSelected ? 'text-[var(--foreground)]' : 'text-[var(--muted-foreground)]'} />
                  {method.label}
                </button>
              );
            })}
          </div>
          {helperText}
        </div>
      )}
      {!showPaymentMethods && helperText}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor={inputId} className="block text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">Cantidad</label>
          {action === 'refund' && (
            <button
              type="button"
              onClick={() => setAmount(String(Number(currentBalance || 0)))}
              disabled={isSubmitting || Number(currentBalance || 0) <= 0}
              className="text-xs font-semibold text-[var(--primary)] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Retirar todo
            </button>
          )}
        </div>
        {quickAmounts && quickAmounts.length > 0 && (
          <div className={`mb-2 grid gap-2 ${quickAmounts.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
            {quickAmounts.map((value) => {
              const isSelected = Number(amount) === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAmount(String(value))}
                  disabled={isSubmitting}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                    isSelected
                      ? 'border-[var(--primary)] bg-white text-[var(--foreground)] ring-1 ring-[var(--primary)]'
                      : 'border-transparent bg-[var(--muted)] text-[var(--foreground)] hover:bg-[#EFEFEF]'
                  } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {value} €
                </button>
              );
            })}
          </div>
        )}
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--muted-foreground)]">€</span>
          <input
            ref={amountRef}
            type="number"
            step="0.01"
            min="0.01"
            id={inputId}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Introducir cantidad manualmente"
            className="block w-full rounded-lg border border-[var(--border)] bg-white py-2 pl-7 pr-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:ring-[var(--primary)]"
            disabled={isSubmitting}
            autoFocus={autoFocus}
          />
        </div>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--muted-foreground)]">
          Saldo actual: <span className="font-semibold text-[var(--foreground)]">€{fmt(base)}</span>
        </span>
        <span className="text-[var(--muted-foreground)]">
          {action === 'refund' ? 'Saldo restante:' : 'Saldo tras añadir'}:{' '}
          <span className="font-semibold text-[var(--foreground)]">€{fmt(projectedBalance)}</span>
        </span>
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full inline-flex justify-center rounded-lg border border-transparent px-4 py-2.5 text-sm font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary)] ${
          isSubmitting ? 'bg-gray-400 text-gray-700 cursor-not-allowed' : 'bg-[var(--primary)] hover:bg-[var(--primary-strong)]'
        }`}
      >
        {isSubmitting ? 'Procesando...' : submitLabel}
      </button>
    </form>
    );
  };

  const memberHeader = memberName ? (
    <div className="wa-modal-header-extra flex items-center gap-3">
      {memberImageUrl ? (
        <img
          src={memberImageUrl}
          alt={memberName}
          className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--muted)] text-sm font-semibold text-[var(--muted-foreground)]">
          {memberName.trim().charAt(0).toUpperCase()}
        </span>
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-[var(--foreground)]">{memberName}</p>
        <p className="mt-0.5 flex items-center gap-2 truncate text-xs text-[var(--muted-foreground)]">
          {memberNumber != null && <span>NºSocio: {memberNumber}</span>}
          {memberStatus && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#F0FDF4] px-2 py-0.5 text-[11px] font-medium text-[#15803D]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
              {memberStatus}
            </span>
          )}
        </p>
      </div>
    </div>
  ) : null;

  const formContent = (
    <div className="space-y-5">
      {localError && (
        <div className="rounded-[var(--radius-md)] border border-[var(--toast-error-border)] bg-[var(--toast-error-bg)] px-4 py-3 text-sm text-[var(--toast-error-text)]" role="alert">
          <span className="block sm:inline">{localError}</span>
        </div>
      )}

      {/* Agregar / Retirar toggle (Figma) — both flows preserved */}
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-[var(--muted)] p-1">
        <button
          type="button"
          onClick={() => { setActiveTab('add'); setLocalError(''); }}
          disabled={isSubmitting}
          className={`rounded-lg py-2 text-sm font-semibold transition-colors ${
            activeTab === 'add' ? 'bg-white text-[var(--foreground)] shadow-sm' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
          }`}
        >
          Agregar saldo
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab('refund'); setLocalError(''); }}
          disabled={isSubmitting}
          className={`rounded-lg py-2 text-sm font-semibold transition-colors ${
            activeTab === 'refund' ? 'bg-white text-[var(--foreground)] shadow-sm' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
          }`}
        >
          Retirar saldo
        </button>
      </div>

      {activeTab === 'add'
        ? renderBalanceForm({
            action: 'add',
            inputId: 'add-balance-amount',
            amount: addQuantity,
            setAmount: setAddQuantity,
            paymentMethod: addPaymentMethod,
            setPaymentMethod: setAddPaymentMethod,
            paymentMethods: PAYMENT_METHODS,
            onSubmit: handleSubmitAdd,
            submitLabel: `Agregar saldo con ${PAYMENT_METHODS.find((item) => item.key === addPaymentMethod)?.label.toLowerCase() || 'efectivo'}`,
            helperText: (
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-[var(--muted)] px-3 py-2.5 text-xs text-[var(--muted-foreground)]">
                <Info size={14} className="mt-0.5 flex-shrink-0" />
                <span>El importe se registrará en la caja abierta del local. Si no hay una única caja abierta, la operación se bloqueará.</span>
              </div>
            ),
            amountRef: inputRef,
            autoFocus: true,
            quickAmounts: QUICK_ADD_AMOUNTS,
          })
        : renderBalanceForm({
            action: 'refund',
            inputId: 'refund-balance-amount',
            amount: refundQuantity,
            setAmount: setRefundQuantity,
            paymentMethod: refundPaymentMethod,
            setPaymentMethod: setRefundPaymentMethod,
            paymentMethods: PAYMENT_METHODS.filter((method) => method.key === 'cash'),
            onSubmit: handleSubmitRefund,
            submitLabel: 'Devolver saldo con efectivo',
            intro: 'Devuelve saldo al socio y descuéntalo de caja. Solo en efectivo.',
            showPaymentMethods: false,
            helperText: (
              <div className="flex items-start gap-2 rounded-lg bg-[var(--muted)] px-3 py-2.5 text-xs text-[var(--muted-foreground)]">
                <Info size={14} className="mt-0.5 flex-shrink-0" />
                <span>El importe se descontará de la caja abierta del local. Si no hay caja abierta, la operación se bloqueará.</span>
              </div>
            ),
            amountRef: null,
            quickAmounts: QUICK_REFUND_AMOUNTS,
            extraInfo: (
              <div className="flex items-center justify-between rounded-lg bg-[var(--muted)] px-4 py-3 text-sm text-[var(--muted-foreground)]">
                <span>Saldo disponible</span>
                <span className="font-semibold text-[var(--foreground)]">
                  {Number(currentBalance || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              </div>
            ),
          })}
    </div>
  );

  const modalContent = showSuccess ? successContent : formContent;
  const modalTitle = showSuccess
    ? (successAction === 'refund' ? 'Saldo devuelto' : 'Crédito añadido')
    : 'Gestión de saldo';

  // Función que previene el cierre cuando se muestra el éxito
  const handleModalClose = () => {
    if (showSuccess) {
      // No permitir cerrar cuando se muestra el mensaje de éxito
      // Solo se puede cerrar con los botones "Deshacer" u "OK"
      return;
    }
    // Cerrar el modal si no está en modo éxito
    if (active) {
      handleAddCashModal();
    }
  };

  // Renderizar el modal usando Portal para que esté fuera del árbol DOM
  if (!active) return null;

  const modalElement = (
    <CustomModal
      active={active}
      handleModal={handleModalClose}
      title={modalTitle}
      size="balance"
      headerExtra={showSuccess ? null : memberHeader}
      children={modalContent}
    />
  );

  // Renderizar en el body usando Portal
  return createPortal(modalElement, document.body);
}
